import React from 'react';
import { BENCHMARK_METRICS } from '../../data/content';
import { DoubleBezelCard } from '../common/DoubleBezelCard';
import { PixelGlyph } from '../common/PixelGlyph';
import { TrendingUp, Award } from 'lucide-react';

export const BenchmarkMatrix: React.FC = () => {
  return (
    <section
      id="benchmarks"
      className="relative py-16 sm:py-24 md:py-32 px-4 sm:px-6 max-w-7xl mx-auto z-10"
      aria-label="Performance Benchmark Matrix"
    >
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between mb-8 sm:mb-16 pb-4 sm:pb-6 border-b border-white/10 gap-6">
        <div>
          <div className="flex items-center gap-2.5 text-xs font-mono text-sunset-gold tracking-ultra uppercase mb-3">
            <PixelGlyph name="grid-dots" size={12} color="#ffaf01" />
            <span>VERIFIED TELEMETRY // BENCHMARK MATRIX</span>
          </div>
          <h2 className="font-display font-extrabold text-3xl sm:text-4xl md:text-5xl text-cream-100">
            Performance that defies compromise.
          </h2>
        </div>
        <div className="flex items-center gap-2 text-[11px] sm:text-xs font-mono text-cream-muted bg-espresso-900/60 px-3.5 py-1.5 sm:px-4 sm:py-2 rounded-full border border-white/10 w-fit">
          <Award className="w-4 h-4 text-sunset-gold shrink-0" />
          <span>AUDITED ON CHROME 128 / SAFARI 18 METAL PIPELINE</span>
        </div>
      </div>

      {/* Grid of 4 Benchmark Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6">
        {BENCHMARK_METRICS.map((metric, idx) => (
          <DoubleBezelCard
            key={idx}
            tilt={true}
            innerClassName="p-5 sm:p-7 md:p-9 flex flex-col justify-between min-h-[220px] sm:min-h-[260px]"
          >
            {/* Metric Top */}
            <div>
              <div className="flex items-center justify-between gap-4 mb-3 sm:mb-4">
                <span className="text-[10px] font-mono tracking-ultra uppercase text-sunset-gold">
                  CATEGORY // {metric.category}
                </span>
                <span className="inline-flex items-center gap-1 text-[11px] sm:text-xs font-mono font-bold text-emerald-400 bg-emerald-400/10 px-2 sm:px-2.5 py-0.5 sm:py-1 rounded-full border border-emerald-400/20">
                  <TrendingUp className="w-3.5 h-3.5" />
                  +{metric.percentageLead}% LEAD
                </span>
              </div>

              <h3 className="font-display font-bold text-lg sm:text-xl md:text-2xl text-cream-100 mb-1.5 sm:mb-2">
                {metric.title}
              </h3>
              <p className="text-xs sm:text-sm text-cream-muted leading-relaxed">
                {metric.highlight}
              </p>
            </div>

            {/* Metric Bottom: Comparison Bars */}
            <div className="pt-4 sm:pt-6 border-t border-white/10 mt-4 sm:mt-6">
              {/* example.th157le.ru Score */}
              <div className="mb-3">
                <div className="flex items-center justify-between text-[11px] sm:text-xs font-mono mb-1.5">
                  <span className="font-bold text-cream-100 flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full bg-sunset-gold" />
                    EXAMPLE (THIS SPECIMEN)
                  </span>
                  <span className="text-sunset-gold font-bold">
                    {metric.exampleValue} {metric.unit}
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-espresso-800 overflow-hidden">
                  <div
                    className="h-full bg-gradient-to-r from-sunset-gold via-sunset-orange to-sunset-flame rounded-full"
                    style={{ width: '96%' }}
                  />
                </div>
              </div>

              {/* Industry Average Score */}
              <div>
                <div className="flex items-center justify-between text-[10px] sm:text-xs font-mono text-cream-dim mb-1.5">
                  <span>STANDARD IMPLEMENTATION</span>
                  <span>
                    {metric.industryAvg} {metric.unit}
                  </span>
                </div>
                <div className="w-full h-2 rounded-full bg-espresso-800 overflow-hidden">
                  <div
                    className="h-full bg-white/20 rounded-full"
                    style={{ width: `${Math.max(15, 100 - metric.percentageLead * 0.4)}%` }}
                  />
                </div>
              </div>
            </div>
          </DoubleBezelCard>
        ))}
      </div>
    </section>
  );
};
